import React, { useRef, useEffect, useState } from "react";
import { galleryItems } from "./gallary.data";
import { GalleryItem, BlogGalleryProps } from "./types";

const BlogGallery: React.FC<BlogGalleryProps> = ({ items = galleryItems }) => {
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const setVideoRef = (id: string, el: HTMLVideoElement | null) => {
    if (el) videoRefs.current[id] = el;
  };

  useEffect(() => {
    if (!isVisible) return;
    Object.values(videoRefs.current).forEach((videoElement) => {
      if (!videoElement) return;
      videoElement.muted = true;
      videoElement.playbackRate = 1;
      videoElement.setAttribute("playsinline", "");
      videoElement.setAttribute("preload", "auto");
      videoElement.play().catch(() => {});
    });
  }, [isVisible]);

  const renderGalleryItem = (item: GalleryItem) => {
    if (item.type === "image") {
      return (
        <div
          key={item.id}
          className="aspect-square overflow-hidden rounded-lg relative"
        >
          <img
            src={item.src}
            alt={item.alt}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ display: "block" }}
          />
        </div>
      );
    } else {
      return (
        <div
          key={item.id}
          className="aspect-square overflow-hidden rounded-lg relative"
        >
          {isVisible && (
            <video
              ref={(el) => setVideoRef(item.id, el)}
              poster={item.poster}
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={item.src} type="video/mp4" />
            </video>
          )}
        </div>
      );
    }
  };

  return (
    <div
      ref={containerRef}
      className="grid w-full h-auto grid-cols-2 gap-4 md:grid-cols-4 my-8"
    >
      {items.map(renderGalleryItem)}
    </div>
  );
};

export default BlogGallery;
